import { Button, FormControlLabel, Grid, IconButton, Switch, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography, useTheme, Box, Card, CardContent, Chip, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { H5, H6 } from 'components/Typography';
import { KeyboardArrowDown } from '@mui/icons-material';
import React, { ChangeEvent, type FC, useEffect, useState, useCallback } from 'react';
import AppSelectField from 'components/input-fields/MultipleChoiceField';
import { agreementApi, contractsApi, experienceLevelApi, rewardApi, seasonApi } from '../../api';
import {
  AgreementEntity,
  CreateContractDto,
  CreateContractDtoLegalContractTypeEnum,
  CreateRewardsDto,
  ExperienceLevelEntity,
  RewardEntity,
} from '../../api/generated';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import FileUploadInput from '../../components/input-fields/FileUploadInput';
import * as Yup from 'yup';
import { ObjectSchema } from 'yup';
import TabBase, { TabResourceProps } from './TabBase';
import CalendarInput from 'components/input-fields/CalendarInput';
import AddAgreememtModal, { AddAgreementFormData } from 'page-sections/data-table/dataTableV2/AddAgreementModal';
import AddIcon from '@mui/icons-material/Add';
import AgreementsDataTable from 'page-sections/data-table/AgreementsDataTable';
import FlexBox from 'components/flexbox/FlexBox';
import { useSeason } from 'contexts/SeasonContext';
import { useEncryption } from 'contexts/EncryptionKeyContext';
import DeleteIcon from 'icons/DeleteIcon';
import { CustomDialog } from 'components/CustomDialog';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import AddRewardModal from 'page-sections/data-table/dataTableV2/AddRewardModal';
import RewardsDataTable from 'page-sections/data-table/RewardsDataTable';
import UploadingOverlay from 'components/UploadOverlay';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { decryptData } from 'utils/encryptionDecryptionAgreements';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import HistoryIcon from '@mui/icons-material/History';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PreContractActions from './PreContractActions';

export type ContractFormData = Omit<CreateContractDto, 'employeeId'> & {
  file?: File;
  filePath?: string | null;
  id: number | undefined;
};

interface ContractFormDataValidationSchema {
  file: File | undefined;
  filePath?: string | null;
  startedOn: string | null | undefined;
  endedOn: string | null | undefined;
  probationPeriodDays: number | null | undefined;
  levelName?: string;
  legalContractType: CreateContractDtoLegalContractTypeEnum;
}

const Contract: FC<TabResourceProps<ContractFormData>> = ({
  data,
  onSubmit,
  onSubmitAgreements,
  id,
  buttonFontSize
}) => {
  const isMobile = window.innerWidth <= 768;
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [experienceLevels, setExperienceLevels] = useState<
    ExperienceLevelEntity[]
  >([]);
  const [isViewing, setIsViewing] = useState(!!data);
  const [dataForAgreements, setDataForAgreements] = useState<ContractFormData | undefined>();
  const [openModal, setOpenModal] = useState(false);
  const [agreements, setAgreements] = useState<AgreementEntity[]>([]);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [legalContractType, setLegalContractType] = useState<CreateContractDtoLegalContractTypeEnum>(
    data?.legalContractType ? data.legalContractType : CreateContractDtoLegalContractTypeEnum.Limited);
  const [agreementData, setAgreementData] = useState<AddAgreementFormData | null>(null);
  const [agreementId, setAgreementId] = useState<number>();
  const [contractId, setContractId] = useState<number | null>(null);
  const [weeklyHours, setWeeklyHours] = useState<number | undefined>(data?.workPercentage ? Math.floor(40 / 100 * data.workPercentage) : 20);
  const { encryptionKey } = useEncryption();
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const isEncryptionMandatory = localStorage.getItem('isEncryptionMandatory');
  const [rewards, setRewards] = useState<RewardEntity[]>([]);
  const [selectedRewards, setSelectedRewards] = useState<any[]>([]);
  const [rewardNames, setRewardNames] = useState<string[]>([]);
  const [documentFilePath, setDocumentFilePath] = useState<string | null>(data?.filePath ?? null);
  const [pastSeasonsHistory, setPastSeasonsHistory] = useState<{
    averageNetDaily: number;
    percentageChange: number;
    hasHistory: boolean;
    pastSeasonsData: Array<{
      seasonId: number;
      seasonName: string;
      agreements: AgreementEntity[];
      averageNetDaily: number;
    }>;
  } | null>(null);


  const { seasonId, getSeason } = useSeason();
  const theme = useTheme();

  const initialValues: ContractFormData = {
    file: data?.file ?? undefined,
    filePath: data?.filePath ?? undefined,
    startedOn: data?.startedOn ?? null,
    endedOn: data?.endedOn ?? null,
    levelName: data?.levelName ?? '',
    typeName: data?.typeName ?? '',
    legalContractType: data?.legalContractType ?? legalContractType,
    seasonId: data?.seasonId ?? seasonId,
    id: data?.id ?? undefined,
    probationPeriodDays: data?.probationPeriodDays ?? null,
    isAllIncluded: data?.isAllIncluded ?? false,
    isFoodIncluded: data?.isFoodIncluded ?? true,
    isHousingIncluded: data?.isHousingIncluded ?? true,
    isPartTime: data?.isPartTime ?? false,
    workPercentage: data?.workPercentage ?? undefined,
    isDraft: data?.isDraft ?? false
  };

  const validationSchema: ObjectSchema<ContractFormDataValidationSchema> = Yup.object().shape({
    file: Yup.mixed<File>(),
    filePath: Yup.string(),
    startedOn: Yup.string().nullable(),
    endedOn: Yup.string().nullable(),
    probationPeriodDays: Yup.number()
      .nullable()
      .min(0, t('contracts.validProbationPeriod')),
    levelName: Yup.string(),
    legalContractType: Yup.mixed<CreateContractDtoLegalContractTypeEnum>()
      .oneOf(Object.values(CreateContractDtoLegalContractTypeEnum)).required(),
    isPartTime: Yup.boolean(),
    workPercentage: Yup.number().when('isPartTime', {
      is: true,
      then: (schema) => schema.required(t('contracts.validPercentage')).min(1, t('contracts.validPercentage')).max(99, t('contracts.validPercentage'))
    }),

  });
  const formikSubmit = (values: ContractFormData) => {
    onSubmit(values, setIsLoading, true);
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: formikSubmit,
  });
  const { values, handleBlur, handleChange, touched, errors, handleSubmit, resetForm, validateForm, setFieldValue, setValues } =
    formik;
  const [dateStarted, setdateStarted] = useState(values.startedOn && values.startedOn !== '' ? new Date(values.startedOn) : null);
  const [dateEnded, setdateEnded] = useState(values.endedOn && values.endedOn !== '' ? new Date(values.endedOn) : null);

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      await formik.setFieldValue('file', files[0]);
    }
  };
  const initializeForm = () => {

    resetForm();
    setValues(initialValues);
    setContractId(data?.id ?? null);
    setdateStarted(data?.startedOn ? new Date(data?.startedOn) : null);
    setdateEnded(data?.endedOn ? new Date(data?.endedOn) : null);
    validateForm();

  }

  const generateDefaultDates = () => {
    const season = getSeason();

    if (!season)
      return;

    const startDate = new Date(season.startDate);
    const endDate = new Date(season.endDate);

    const utcStartDate = new Date(Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate()));
    formik.setFieldValue('startedOn', utcStartDate?.toISOString(), true);
    setdateStarted(utcStartDate);

    if (values.legalContractType !== 'UNLIMITED') {
      const utcEndDate = new Date(Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate()));
      formik.setFieldValue('endedOn', utcEndDate?.toISOString(), true);
      setdateEnded(utcEndDate);
    }
  }

  const fetchAgreements = () => {
    agreementApi.findAll(id, seasonId).then(({ data }) => {
      setAgreements(data.sort((firstElement, secondElement) => new Date(firstElement.startedOn).valueOf() - new Date(secondElement.startedOn).valueOf()));
    });
  };

  const fetchPastSeasonsHistory = useCallback(async () => {
    if (!id || !seasonId) return;

    try {
      // Get all seasons
      const { data: allSeasons } = await seasonApi.findAll();
      const currentSeason = allSeasons.find(s => s.id === seasonId);
      if (!currentSeason) return;

      // Get past seasons (before current season)
      const pastSeasons = allSeasons.filter(s => {
        const seasonEndDate = new Date(s.endDate);
        const currentStartDate = new Date(currentSeason.startDate);
        return seasonEndDate < currentStartDate;
      }).sort((a, b) => new Date(b.endDate).getTime() - new Date(a.endDate).getTime());

      if (pastSeasons.length === 0) {
        setPastSeasonsHistory({ 
          averageNetDaily: 0, 
          percentageChange: 0, 
          hasHistory: false,
          pastSeasonsData: []
        });
        return;
      }

      // Fetch agreements from all past seasons (without contractId filter to get all past agreements)
      const pastAgreementsPromises = pastSeasons.map(async (season) => {
        const { data } = await agreementApi.findAll(id, season.id, undefined);
        return { season, agreements: data };
      });
      const pastAgreementsResults = await Promise.all(pastAgreementsPromises);
      
      // Filter out seasons with no agreements
      const seasonsWithAgreements = pastAgreementsResults.filter(result => result.agreements.length > 0);

      if (seasonsWithAgreements.length === 0) {
        setPastSeasonsHistory({ 
          averageNetDaily: 0, 
          percentageChange: 0, 
          hasHistory: false,
          pastSeasonsData: []
        });
        return;
      }

      // Calculate average net daily from past seasons and store per-season data
      let totalNetDaily = 0;
      let count = 0;
      const pastSeasonsData = seasonsWithAgreements.map(({ season, agreements }) => {
        let seasonTotal = 0;
        let seasonCount = 0;
        
        agreements.forEach(agreement => {
          let netDaily = 0;
          if (agreement.net_daily_encrypted && encryptionKey && encryptionKey !== '') {
            const net = decryptData(agreement.net_daily_encrypted, encryptionKey);
            if (net !== '') {
              netDaily = parseFloat(String(net));
            }
          } else if (agreement.net_daily !== null && agreement.net_daily !== undefined) {
            netDaily = parseFloat(String(agreement.net_daily));
          }
          if (netDaily > 0) {
            seasonTotal += netDaily;
            seasonCount++;
            totalNetDaily += netDaily;
            count++;
          }
        });

        return {
          seasonId: season.id,
          seasonName: season.name,
          agreements: agreements.sort((a, b) => new Date(a.startedOn).getTime() - new Date(b.startedOn).getTime()),
          averageNetDaily: seasonCount > 0 ? seasonTotal / seasonCount : 0
        };
      });

      const pastAverage = count > 0 ? totalNetDaily / count : 0;

      // Calculate current season average
      let currentTotal = 0;
      let currentCount = 0;
      agreements.forEach(agreement => {
        let netDaily = 0;
        if (agreement.net_daily_encrypted && encryptionKey && encryptionKey !== '') {
          const net = decryptData(agreement.net_daily_encrypted, encryptionKey);
          if (net !== '') {
            netDaily = parseFloat(String(net));
          }
        } else if (agreement.net_daily !== null && agreement.net_daily !== undefined) {
          netDaily = parseFloat(String(agreement.net_daily));
        }
        if (netDaily > 0) {
          currentTotal += netDaily;
          currentCount++;
        }
      });

      const currentAverage = currentCount > 0 ? currentTotal / currentCount : 0;

      // Calculate percentage change
      let percentageChange = 0;
      if (pastAverage > 0) {
        if (currentAverage > 0) {
          percentageChange = ((currentAverage - pastAverage) / pastAverage) * 100;
        } else {
          // If current average is 0 but past average exists, show -100%
          percentageChange = -100;
        }
      } else if (currentAverage > 0 && pastAverage === 0) {
        // If past average is 0 but current average exists, show +100% (or a large increase)
        percentageChange = 100;
      }

      // Show history if we have past agreements, even if we can't calculate averages
      setPastSeasonsHistory({
        averageNetDaily: pastAverage,
        percentageChange,
        hasHistory: true,
        pastSeasonsData
      });
    } catch (error) {
      console.error('Error fetching past seasons history:', error);
      setPastSeasonsHistory({ 
        averageNetDaily: 0, 
        percentageChange: 0, 
        hasHistory: false,
        pastSeasonsData: []
      });
    }
  }, [id, seasonId, contractId, encryptionKey, agreements]);

  const fetchRewards = () => {
    if (!data?.id)
      return;
    rewardApi.findByContract(data?.id).then((data) => {
      setRewards(data.data);
      setSelectedRewards([]);
      rewardApi.findAllNames().then(({ data }) => { setRewardNames(data.map(value => value.name)) });
    });

  }

  const handleCheckedChange = (event: React.ChangeEvent<HTMLInputElement>, field: string) => {
    formik.setFieldValue(field, (event.target.checked), true)
  }

  const handlePercentageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value);
    if (!isNaN(value) || event.target.value === '') {
      formik.setFieldValue('workPercentage', event.target.value, true);
      const hours = Math.round(40 / 100 * value);
      setWeeklyHours(hours);
    }

  }

  const handleHourChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value);
    if (!isNaN(value)) {
      setWeeklyHours(value);
      const percentage = value / 40 * 100;
      formik.setFieldValue('workPercentage', percentage.toFixed(2), true);
    }
    else {
      setWeeklyHours(undefined);
      formik.setFieldValue('workPercentage', 0, true);
      formik.setFieldValue('workPercentage', null, true);
    }
  }

  const handleRowSelect = (rowArr: []) => {
    setSelectedRows(rowArr);
  };

  const ids = selectedRows.map((item: any) => item.original.id);
  const handleDelete = async () => {
    const deleteActions = ids.map(async (id) => agreementApi.remove(id));
    await Promise.all(deleteActions);
    fetchAgreements();
  };
  const handleAddAgreement = (id: number, data?: AddAgreementFormData | null) => {
    setAgreementData(data ?? null);
    setAgreementId(id);
    setOpenModal(true);
  }
  const handleAddAgreementClick = async () => {
    // update contract in any case
    const submittedContractId = await onSubmit(values, setIsLoading, false);
    if (data?.id && !contractId) {
      setContractId(data?.id);
    } else if (!contractId) {
      setContractId(submittedContractId);
    }
    setOpenModal(true);

  };

  const handleChangeButton = (
    event: React.MouseEvent<HTMLElement>,
    contractType: CreateContractDtoLegalContractTypeEnum,
  ) => {
    setLegalContractType(contractType);
    values.legalContractType = contractType;
  };

  useEffect(() => {
    setDataForAgreements(values)
  }, [values]);

  useEffect(() => {
    initializeForm();
  }, [data]);

  useEffect(() => {
    Promise.all([
      experienceLevelApi.findAll().then(({ data }) => {
        setExperienceLevels(data);
      }),
      agreementApi.findAll(id, seasonId).then(({ data }) => {
        setAgreements(data.sort((firstElement, secondElement) => new Date(firstElement.startedOn).valueOf() - new Date(secondElement.startedOn).valueOf()));
      }),
    ]);
    fetchRewards();
  }, []);

  useEffect(() => {
    const isNotSettings = window.location.hash !== '#settings';
    if (isNotSettings) {
      agreementApi.findAll(id, seasonId).then(({ data }) => {
        setAgreements(data.sort((firstElement, secondElement) => new Date(firstElement.startedOn).valueOf() - new Date(secondElement.startedOn).valueOf()));
      });
      initializeForm();
      resetForm();
    }
  }, [seasonId, window.location.hash]);

  useEffect(() => {
    if (id && seasonId) {
      fetchPastSeasonsHistory();
    } else {
      setPastSeasonsHistory(null);
    }
  }, [id, seasonId, contractId, encryptionKey, agreements, fetchPastSeasonsHistory]);

  const titleComponent = (
    <Grid container justifyContent="space-between" alignItems="center">
      <Grid item>
        <H5 padding={3} style={{ fontSize: '15px' }} >{t('employees.contract.title')}</H5>
      </Grid>
      <Grid item marginRight={'auto'}>
        <Button variant='outlined'

          color={values.isDraft ? 'warning' : 'success'}
          style={{ color: values.isDraft ? theme.palette.warning.main : theme.palette.success.main }}

          onClick={() => {
            setFieldValue('isDraft', !values.isDraft);
          }}> {values.isDraft ? t('employees.contract.draft') : t('employees.contract.confirmed')}</Button>
      </Grid>
      <Grid item>
        {data?.id && (
          <Button
            variant="contained"
            onClick={() => {
              setDeleteConfirmationOpen(true);
            }}
            style={{ backgroundColor: 'red', color: 'white', marginLeft: '1rem' }}
            startIcon={<DeleteIcon />}
          >
            {t('employees.contract.delete')}
          </Button>
        )}
      </Grid>
    </Grid>
  );


  const handleCloseDeleteConfirmation = () => {
    setDeleteConfirmationOpen(false);
  };


  // Handle confirm delete
  const handleConfirmDelete = async () => {
    if (data?.id) {
      await contractsApi.deleteContract(data.id);
      data = undefined;
      initializeForm();
      setDeleteConfirmationOpen(false);
    }
  };
  const [openRewardModal, setOpenRewardModal] = useState(false);

  const handleOpenRewardModal = async () => {
    if (data?.id && !contractId) {
      setContractId(data?.id);
    } else if (!contractId) {
      const submittedContractId = await onSubmit(values, setIsLoading, false);
      setContractId(submittedContractId);
    }
    setOpenRewardModal(true)
  };

  const handleCloseRewardModal = () => { setOpenRewardModal(false) };
  const handleAddReward = async (values: any) => {
    if (!contractId)
      return;
    const rewardDTO: CreateRewardsDto = {
      amount: values.amount,
      earnedOn: values.date,
      name: values.rewardName,
      notes: values.notes,
      contractId
    }
    await rewardApi.createRewards(rewardDTO).then(() => { fetchRewards() });
    handleCloseRewardModal();
  };

  const handleDeleteRewards = async () => {
    const deleteActions = selectedRewards.map(async rewardId => {
      await rewardApi.deleteRewards(rewardId.original.id);
    });
    await Promise.all(deleteActions).then(() => {
      fetchRewards();

    });


  };


  const handleRemoveDocument = async () => {

    const updateContractDto: any = {
      filePath: null,
    };

    if (contractId)
      await contractsApi.update(contractId.toString(), updateContractDto);
    setFieldValue('file', undefined);
    setFieldValue('filePath', undefined);
    setDocumentFilePath(null);
  };

  return (
    <>
      <TabBase
        title={t('employees.contract.title')}
        setIsViewing={setIsViewing}
        isViewing={isViewing}
        handleSubmit={handleSubmit}
        titleComponent={titleComponent}
      >
        {isLoading &&
          <UploadingOverlay />
        }

        <ToggleButtonGroup
          orientation={isMobile ? 'vertical' : 'horizontal'}
          color="primary"
          value={legalContractType}
          exclusive
          onChange={handleChangeButton}
          aria-label="Platform"
          fullWidth
          sx={{ paddingBottom: '1.5rem' }}
          disabled={isViewing}
        >
          <ToggleButton value={CreateContractDtoLegalContractTypeEnum.Limited} disabled={legalContractType === CreateContractDtoLegalContractTypeEnum.Limited}>
            {t("employees.contract.types.limited")}
          </ToggleButton>
          <ToggleButton value={CreateContractDtoLegalContractTypeEnum.Unlimited} disabled={legalContractType === CreateContractDtoLegalContractTypeEnum.Unlimited}>
            {t("employees.contract.types.unlimited")}
          </ToggleButton>
          <ToggleButton value={CreateContractDtoLegalContractTypeEnum.Internship} disabled={legalContractType === CreateContractDtoLegalContractTypeEnum.Internship}>
            {t("employees.contract.types.internship")}
          </ToggleButton>
          <ToggleButton value={CreateContractDtoLegalContractTypeEnum.Call} disabled={legalContractType === CreateContractDtoLegalContractTypeEnum.Call}>
            {t("employees.contract.types.call")}
          </ToggleButton>
          <ToggleButton value={CreateContractDtoLegalContractTypeEnum.Apprenticeship} disabled={legalContractType === CreateContractDtoLegalContractTypeEnum.Apprenticeship}>
            {t("employees.contract.types.apprenticeship")}
          </ToggleButton>
        </ToggleButtonGroup>

        <Grid container spacing={5}>
          <Grid item container xs={10} spacing={1}>
            <Grid item sm={6} xs={12}>
              <CalendarInput
                disabled={isViewing}
                format='dd.MM.yyyy'
                label={t('employees.contract.startingDate')}
                disableFuture={false}
                localeText={{
                  fieldDayPlaceholder: () => t("DD"),
                  fieldMonthPlaceholder: () => t("MM"),
                  fieldYearPlaceholder: () => t("YYYY"),
                }}
                value={dateStarted}
                onChange={(newValue) => {
                  if (newValue && !isNaN(new Date(newValue).getTime())) {
                    const utcDate = new Date(Date.UTC(newValue.getFullYear(), newValue.getMonth(), newValue.getDate()));
                    formik.setFieldValue('startedOn', utcDate?.toISOString(), true);
                    setdateStarted(newValue);
                  }
                }}
                slotProps={{
                  textField: {
                    helperText: t("DD") + "." + t("MM") + "." + t("YYYY")
                  },
                }}
              />
            </Grid>
            {(legalContractType !== CreateContractDtoLegalContractTypeEnum.Unlimited || !isMobile) && (
              <Grid item sm={6} xs={12} >
                {legalContractType !== CreateContractDtoLegalContractTypeEnum.Unlimited && (

                  <CalendarInput
                    disabled={isViewing}
                    format='dd.MM.yyyy'
                    label={t('employees.contract.expiryDate')}
                    disableFuture={false}
                    localeText={{
                      fieldDayPlaceholder: () => t("DD"),
                      fieldMonthPlaceholder: () => t("MM"),
                      fieldYearPlaceholder: () => t("YYYY"),
                    }}
                    value={dateEnded}
                    onChange={(newValue) => {
                      if (newValue && !isNaN(new Date(newValue).getTime())) {
                        const utcDate = new Date(Date.UTC(newValue.getFullYear(), newValue.getMonth(), newValue.getDate()));
                        formik.setFieldValue('endedOn', utcDate?.toISOString(), true);
                        setdateEnded(newValue);
                      }
                    }}
                    slotProps={{
                      textField: {
                        helperText: t("DD") + "." + t("MM") + "." + t("YYYY")
                      },
                    }}
                  />

                )}
              </Grid>
            )}
          </Grid>

          <Grid item sm={1} xs={1} justifyContent={'center'} display={'flex'} paddingBottom={3}>
            <Tooltip title={t('employees.contract.generateDates')} placement='bottom'>
              <Button onClick={generateDefaultDates} variant='contained' disabled={isViewing}>
                <DateRangeIcon sx={{
                  display: 'flex',
                  width: '100%',
                }} />
              </Button>
            </Tooltip>
          </Grid>
          <Grid item sm={6} xs={12}>
            <TextField
              disabled={isViewing}
              label={t('employees.contract.probationPeriodDays')}
              value={values.probationPeriodDays ?? ''}
              onChange={async (e) => setFieldValue('probationPeriodDays', e.target.value ? parseInt(e.target.value, 10) : null, true)}
              type="number"
              InputProps={{
                endAdornment: <Typography>{t('employees.contract.days')}</Typography>,
              }}
              helperText={touched.probationPeriodDays && (errors.probationPeriodDays as string)}
              error={Boolean(touched.probationPeriodDays && (errors.probationPeriodDays as string))}
            />
          </Grid>
          <Grid item sm={6} xs={12}>
            <AppSelectField
              disabled={isViewing}
              InputLabelProps={{ shrink: true }}
              select
              fullWidth
              name='levelName'
              variant='outlined'
              label={t('employees.contract.experienceLevel.label')}
              placeholder=''
              SelectProps={{
                native: true,
                IconComponent: KeyboardArrowDown,
              }}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.levelName}
              helperText={touched.levelName && (errors.levelName as string)}
              error={Boolean(touched.levelName && (errors.levelName as string))}
            >
              <option value={''}>
                {t('employees.contract.experienceLevel.default')}
              </option>

              {experienceLevels.map((experienceLevel) => (
                <option key={experienceLevel.name} value={experienceLevel.name}>
                  {experienceLevel.name}
                </option>
              ))}
            </AppSelectField>
          </Grid>

          <Grid item xs={12} container>
            <Grid item sm={6} xs={12}>
              <FormControlLabel
                sx={{ minWidth: '0px' }}
                label={<Typography noWrap textOverflow={'ellipsis'} overflow={'hidden'}>{t('employees.contract.allIncluded')}</Typography>}
                labelPlacement='end'
                control=
                {<Switch
                  disabled={isViewing}
                  checked={values.isAllIncluded}
                  onChange={(e) => { handleCheckedChange(e, 'isAllIncluded') }}
                  inputProps={{ 'aria-label': 'controlled' }}
                />}
              />
            </Grid>

            <Grid item sm={6} xs={12}>
              <FormControlLabel
                sx={{ minWidth: '0px' }}
                label={<Typography noWrap textOverflow={'ellipsis'} overflow={'hidden'}>{t('employees.contract.foodIncluded')}</Typography>}
                labelPlacement='end'
                control=
                {<Switch
                  disabled={isViewing}
                  checked={values.isFoodIncluded}
                  onChange={(e) => { handleCheckedChange(e, 'isFoodIncluded') }}
                  inputProps={{ 'aria-label': 'controlled' }}
                />}
              />
            </Grid>

            <Grid item sm={6} xs={12}>
              <FormControlLabel
                sx={{ minWidth: '0px' }}
                label={<Typography noWrap textOverflow={'ellipsis'} overflow={'hidden'}>{t('employees.contract.housingIncluded')}</Typography>}
                labelPlacement='end'
                control=
                {<Switch
                  disabled={isViewing}
                  checked={values.isHousingIncluded}
                  onChange={(e) => { handleCheckedChange(e, 'isHousingIncluded') }}
                  inputProps={{ 'aria-label': 'controlled' }}
                />}
              />
            </Grid>

          </Grid>

          <Grid item sm={6} xs={6} alignSelf={'end'}>
            <FormControlLabel
              sx={{ minWidth: '0px' }}
              label={<Typography noWrap textOverflow={'ellipsis'} overflow={'hidden'}>{t('shift.partTime')}</Typography>}
              labelPlacement='end'
              control=
              {<Switch
                disabled={isViewing}
                checked={values.isPartTime}
                onChange={(e) => {
                  handleCheckedChange(e, 'isPartTime');
                  if (e.target.value) {
                    setFieldValue('workPercentage', 50);
                    setWeeklyHours(20);
                  }
                }}
                inputProps={{ 'aria-label': 'controlled' }}
              />}
            />
          </Grid>


          {values.isPartTime && (
            <>
              <Grid item sm={3} xs={3}>
                <TextField
                  variant='standard'
                  fullWidth
                  id="standard-number"
                  label={t('contracts.workPercentage')}
                  value={values.workPercentage}
                  //  required={values.isPartTime}
                  onChange={handlePercentageChange}
                  //  type='number'
                  disabled={isViewing || !values.isPartTime}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={
                    {
                      endAdornment: <>%</>
                    }
                  }
                  helperText={formik.errors.workPercentage}
                  error={Boolean(formik.errors.workPercentage)}
                />
              </Grid>
              <Grid item sm={3} xs={3}>
                <TextField
                  variant='standard'
                  fullWidth
                  label={t('employees.contract.weeklyHours')}
                  value={weeklyHours}
                  onChange={handleHourChange}
                  type='number'
                  disabled={isViewing || !values.isPartTime}

                />
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <H6 mb={1}>{t('employees.contract.contractFile')}</H6>
            {documentFilePath ? (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <a target='_blank' rel='noopener noreferrer' href={documentFilePath}>
                  {t('employees.contract.contractFile')}
                </a>
                <IconButton
                  disabled={isViewing}
                  style={{ marginLeft: '10px' }}
                  onClick={handleRemoveDocument}>
                  <CancelOutlinedIcon />
                </IconButton>
              </div>
            ) : (
              <FileUploadInput disabled={isViewing} handleOnChange={handleFileChange} maxDimension={100} />
            )}
          </Grid>

          <Grid item xs={12}>
            <PreContractActions employeeId={id} contractId={contractId} />
          </Grid>
        </Grid>
      </TabBase>


      <TabBase
        title={t('employees.agreements.title')}
        setIsViewing={setIsViewing}
        dontShowMoveToNextToConfirm
        isViewing={false}
        handleSubmit={handleSubmit}
      >
        <AddAgreememtModal
          edit={!!agreementData}
          data={agreementData}
          open={openModal}
          id={agreementId}
          agreements={agreements}
          contractData={dataForAgreements}
          contractId={contractId}
          onClose={() => {
            setOpenModal(false);
            setAgreementId(undefined);
            setAgreementData(null);
            fetchAgreements();
          }}
          // onSubmitAgreements callback is always set in Contract.tsx - by definition. Is optional only because we use the same TabBase of other tab
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          onSubmit={onSubmitAgreements!}
        />
        {selectedRows.length > 0 && agreements.length > 0 && (
          <FlexBox alignItems='center' justifyContent={'flex-end'}>
            <H6 mr={1}>
              {t('common.tables.select.numSelected', {
                num: selectedRows.length,
              })}
            </H6>

            <Button
              size='small'
              color='error'
              variant='contained'
              onClick={handleDelete}
              sx={{ color: 'common.white' }}
            >
              {t('common.tables.select.deleteSelected')}
            </Button>
          </FlexBox>
        )}

        {pastSeasonsHistory && (
          <Box sx={{ mb: 3 }}>
            {pastSeasonsHistory.hasHistory ? (
              <Accordion 
                defaultExpanded={false}
                sx={{ 
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  boxShadow: 'none',
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': {
                    margin: 0
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}
                  sx={{
                    px: 2,
                    py: 1.5,
                    '& .MuiAccordionSummary-content': {
                      my: 0
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                    <HistoryIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                          {t('employees.agreements.pastSeasons.average')}
                        </Typography>
                        <Typography variant="body1" fontWeight={700} color="text.primary">
                          {pastSeasonsHistory.averageNetDaily.toFixed(2)} €
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                          {t('employees.agreements.pastSeasons.change')}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {pastSeasonsHistory.percentageChange >= 0 ? (
                            <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main' }} />
                          ) : (
                            <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main' }} />
                          )}
                          <Chip
                            label={`${pastSeasonsHistory.percentageChange >= 0 ? '+' : ''}${pastSeasonsHistory.percentageChange.toFixed(1)}%`}
                            size="small"
                            sx={{
                              bgcolor: pastSeasonsHistory.percentageChange >= 0 ? 'success.main' : 'error.main',
                              color: 'white',
                              fontSize: '0.7rem',
                              height: '22px',
                              fontWeight: 700
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                  <Box sx={{ mt: 2 }}>
                    {pastSeasonsHistory.pastSeasonsData.map((seasonData, index) => (
                      <Box 
                        key={seasonData.seasonId} 
                        sx={{ 
                          mb: index < pastSeasonsHistory.pastSeasonsData.length - 1 ? 2 : 0,
                          pb: index < pastSeasonsHistory.pastSeasonsData.length - 1 ? 2 : 0,
                          borderBottom: index < pastSeasonsHistory.pastSeasonsData.length - 1 ? '1px solid' : 'none',
                          borderColor: 'divider'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                          <CalendarTodayIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                          <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                            {seasonData.seasonName}
                          </Typography>
                          <Chip
                            label={`${seasonData.agreements.length} ${seasonData.agreements.length === 1 ? t('employees.agreements.name') : t('employees.agreements.title')}`}
                            size="small"
                            sx={{
                              bgcolor: 'primary.light',
                              color: 'primary.contrastText',
                              fontSize: '0.65rem',
                              height: '20px',
                              fontWeight: 600
                            }}
                          />
                          {seasonData.averageNetDaily > 0 && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                              {t('employees.agreements.pastSeasons.average')}: <strong>{seasonData.averageNetDaily.toFixed(2)} €</strong>
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ pl: 4 }}>
                          {seasonData.agreements.map((agreement) => {
                            let netDaily = 0;
                            let isEncrypted = false;
                            if (agreement.net_daily_encrypted && encryptionKey && encryptionKey !== '') {
                              const net = decryptData(agreement.net_daily_encrypted, encryptionKey);
                              if (net !== '') {
                                netDaily = parseFloat(String(net));
                              } else {
                                isEncrypted = true;
                              }
                            } else if (agreement.net_daily !== null && agreement.net_daily !== undefined) {
                              netDaily = parseFloat(String(agreement.net_daily));
                            }

                            const startDate = new Date(agreement.startedOn);
                            const endDate = agreement.endedOn ? new Date(agreement.endedOn) : null;

                            return (
                              <Box
                                key={agreement.id}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 2,
                                  py: 1,
                                  px: 1.5,
                                  borderRadius: 1,
                                  bgcolor: 'grey.50',
                                  mb: 1
                                }}
                              >
                                <Box sx={{ minWidth: 100 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                    {t('employees.agreements.field.startDate')}
                                  </Typography>
                                  <Typography variant="body2" fontWeight={500} fontSize="0.75rem">
                                    {startDate.toLocaleDateString('it-IT')}
                                  </Typography>
                                </Box>
                                {endDate && (
                                  <Box sx={{ minWidth: 100 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                      {t('employees.agreements.field.endDate')}
                                    </Typography>
                                    <Typography variant="body2" fontWeight={500} fontSize="0.75rem">
                                      {endDate.toLocaleDateString('it-IT')}
                                    </Typography>
                                  </Box>
                                )}
                                {!endDate && (
                                  <Chip
                                    label={t('employees.contract.types.unlimited')}
                                    size="small"
                                    sx={{
                                      bgcolor: 'info.main',
                                      color: 'white',
                                      fontSize: '0.65rem',
                                      height: '20px',
                                      fontWeight: 500
                                    }}
                                  />
                                )}
                                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {isEncrypted ? (
                                    <Tooltip title={t('encryption.tooltip')} arrow>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          filter: 'blur(3px)',
                                          opacity: 0.7,
                                          fontWeight: 500,
                                          fontSize: '0.75rem'
                                        }}
                                      >
                                        ・・・ €
                                      </Typography>
                                    </Tooltip>
                                  ) : netDaily > 0 ? (
                                    <Box
                                      sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: 1,
                                        bgcolor: 'success.main',
                                        color: 'white'
                                      }}
                                    >
                                      <Typography variant="body2" fontWeight={700} fontSize="0.75rem">
                                        {netDaily.toFixed(2)} €
                                      </Typography>
                                    </Box>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary" fontSize="0.75rem" fontStyle="italic">
                                      -
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ) : (
              <Card 
                variant="outlined" 
                sx={{ 
                  bgcolor: 'grey.50',
                  borderColor: 'divider',
                  borderRadius: 2
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HistoryIcon sx={{ fontSize: 20, color: 'text.disabled' }} />
                    <Typography variant="body2" color="text.secondary" fontStyle="italic">
                      {t('employees.agreements.pastSeasons.noHistory')}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>
        )}

        {agreements.length > 0 && (
          <AgreementsDataTable
            editAgreementCallback={handleAddAgreement}
            data={agreements}
            handleRowSelect={handleRowSelect}
            encryptionKey={encryptionKey}
          />
        )}

        <Grid container justifyContent="center" paddingTop={1}>
          <Button
            disabled={!(
              (dateStarted !== null && (dateEnded !== null || legalContractType === CreateContractDtoLegalContractTypeEnum.Unlimited)) &&
              !(isEncryptionMandatory === 'true' && encryptionKey === '')
            )}
            variant="contained"
            color="primary"
            type='button'
            startIcon={<AddIcon />}
            onClick={handleAddAgreementClick}
            style={{ fontSize: buttonFontSize }}
          >
            {t('common.tables.button.addItem', {
              item: t('employees.agreements.title'),
            })}
          </Button>
        </Grid>

      </TabBase >
      <CustomDialog
        open={deleteConfirmationOpen}
        onClose={handleCloseDeleteConfirmation}
        onConfirm={handleConfirmDelete}
        title={t('dialog.title', {
          item: t("contracts.itemName"),
        })}
        content={t('dialog.confirmContent.removeContract')}
        icon={<DeleteForeverIcon />}
        confirmText={t('dialog.confirm')}
        confirmColor='error'
        cancelColor='primary'
      />

      <TabBase
        title={t('employees.rewards.title')}
        dontShowMoveToNextToConfirm
        setIsViewing={setIsViewing}
        isViewing={false}
        handleSubmit={handleSubmit}
      >
        {selectedRewards.length > 0 && (
          <FlexBox alignItems='center' justifyContent={'flex-end'}>
            <H6 mr={1}>
              {t('common.tables.select.numSelected', {
                num: selectedRewards.length,
              })}
            </H6>
            <Button
              size='small'
              color='error'
              variant='contained'
              onClick={handleDeleteRewards}
              sx={{ color: 'common.white' }}
            >
              {t('common.tables.select.deleteSelected')}
            </Button>
          </FlexBox>
        )}

        {rewards.length > 0 && (
          <RewardsDataTable
            data={rewards}
            handleRowSelect={(selectedIds) => { setSelectedRewards(selectedIds) }}
          />
        )}

        <Grid container justifyContent="center" paddingTop={1}>
          <Button
            disabled={!(
              (dateStarted !== null && (dateEnded !== null || legalContractType === CreateContractDtoLegalContractTypeEnum.Unlimited)) &&
              !(isEncryptionMandatory === 'true' && encryptionKey === '')
            )}
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenRewardModal}
            style={{ fontSize: buttonFontSize }}
          >
            {t('common.tables.button.addItem', { item: t('employees.rewards.title') })}
          </Button>
        </Grid>
      </TabBase>

      <AddRewardModal open={openRewardModal} onClose={handleCloseRewardModal} onSubmit={handleAddReward} fetchRewards={fetchRewards} usedNames={rewardNames} />
    </>

  );
};

export default Contract;